import React, {useCallback, useEffect, useRef, useState} from "react";
import classnames from "classnames";
import {useShallow} from "zustand/react/shallow";

import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import StorefrontIcon from "@mui/icons-material/Storefront";
import CircularProgress from "@mui/material/CircularProgress";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import {useDebounce} from "@uidotdev/usehooks";

import {BaseModal} from "../../components/BaseModal";
import fetcher from "../../fetcher";
import {useThemeStore} from "../../stores/themeStore";
import {useSavedDealershipsStore} from "../../stores/savedDealershipsStore";

import "./DealershipsModal.scss";

// The BaseModal wrapper stays transparent; the .DealershipsModal card paints its
// own themed surface (data-theme selects the palette block in SCSS).
const wrapperStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: 'transparent',
    boxShadow: 'none',
    outline: 'none',
    padding: 0,
};

const ROWS = 25

// Themed dropdown menu — same convention as the DealPage/Calc selects: drop the
// menu below the control and paint the portaled Paper via a global class
// (.dm-select-paper, keyed off html[data-theme] since the menu portals out).
const selectMenuProps = {
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
    marginThreshold: 4,
    PaperProps: { className: 'dm-select-paper' },
}

// Authored in rough popularity order; the dropdown renders BRAND_OPTIONS (below)
// so new entries can be appended here without worrying about placement.
const BRANDS = [
    'Toyota', 'Honda', 'Ford', 'Chevrolet', 'BMW', 'Mercedes-Benz', 'Audi',
    'Kia', 'Hyundai', 'Nissan', 'Volkswagen', 'Subaru', 'Mazda', 'Jeep', 'Ram',
    'GMC', 'Lexus', 'Acura', 'Infiniti', 'Genesis', 'Volvo', 'Porsche', 'Tesla',
    'Chrysler', 'Dodge', 'Buick', 'Cadillac', 'Lincoln',
]

// Sorts a copy, never the input. Case-insensitive on purpose: a default sort
// compares char codes, which would put GMC ahead of Genesis ('M' < 'e').
export const sortBrandsAlphabetically = (brands) =>
    [...brands].sort((a, b) => a.localeCompare(b, undefined, {sensitivity: 'base'}))

// Sorted once at module load — the list is static, so there's nothing to redo
// per render. "All Brands" is rendered separately and stays pinned to the top.
const BRAND_OPTIONS = sortBrandsAlphabetically(BRANDS)

// Backend dealer search accepts a single `state` (2-letter code).
const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
]

export const DealershipsModal = ( { isOpen, setIsOpen } ) => {
    const theme = useThemeStore((state) => state.theme)
    const {saved, fetchSaved, saveDealership, removeDealership} = useSavedDealershipsStore(
        useShallow((state) => ({
            saved: state.saved,
            fetchSaved: state.fetchSaved,
            saveDealership: state.saveDealership,
            removeDealership: state.removeDealership,
        }))
    )

    const [query, setQuery] = useState('')
    const [makeFilter, setMakeFilter] = useState('')
    const [stateFilter, setStateFilter] = useState('')

    const [dealers, setDealers] = useState([])
    const [count, setCount] = useState(0)
    const [nextOffset, setNextOffset] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState(false)

    const debouncedQuery = useDebounce(query, 400)
    const reqIdRef = useRef(0)
    const resultsRef = useRef(null)
    const sentinelRef = useRef(null)
    const loadMoreRef = useRef(() => {})

    // Load the saved set so the right pane + Select buttons reflect state.
    useEffect(() => { fetchSaved() }, [fetchSaved])

    const buildParams = useCallback((start) => {
        const params = new URLSearchParams()
        if (debouncedQuery.trim()) params.append('q', debouncedQuery.trim())
        if (makeFilter) params.append('make', makeFilter)
        if (stateFilter) params.append('state', stateFilter)
        params.append('start', String(start))
        params.append('rows', String(ROWS))
        return params.toString()
    }, [debouncedQuery, makeFilter, stateFilter])

    // A ref guard drops stale responses so fast filter changes can't overwrite
    // newer results out of order.
    const runSearch = useCallback(async (start, append) => {
        const reqId = ++reqIdRef.current
        setIsLoading(true)
        setError(false)
        try {
            const res = await fetcher.searchDealerships(buildParams(start))
            if (reqId !== reqIdRef.current) return
            const data = res?.data || {}
            if (append) setDealers((prev) => [...prev, ...(data.dealers || [])])
            else setDealers(data.dealers || [])
            setCount(data.count ?? 0)
            setNextOffset(data.next_offset ?? null)
        } catch (e) {
            if (reqId !== reqIdRef.current) return
            setError(true)
            if (!append) { setDealers([]); setCount(0); setNextOffset(null) }
        } finally {
            if (reqId === reqIdRef.current) setIsLoading(false)
        }
    }, [buildParams])

    // Live search: re-run from the top whenever a filter changes.
    useEffect(() => { runSearch(0, false) }, [runSearch])

    const loadMore = () => {
        if (nextOffset != null && !isLoading) runSearch(nextOffset, true)
    }
    // Keep the observer callback pointed at the latest loadMore (fresh
    // nextOffset / isLoading) without re-creating the observer.
    loadMoreRef.current = loadMore

    // Infinite scroll: load the next page when the bottom sentinel scrolls into
    // view. Re-attached whenever the list appears/disappears.
    const hasResults = dealers.length > 0
    useEffect(() => {
        const root = resultsRef.current
        const target = sentinelRef.current
        if (!root || !target) return
        const observer = new IntersectionObserver(
            (entries) => { if (entries[0].isIntersecting) loadMoreRef.current() },
            { root, rootMargin: '200px' }
        )
        observer.observe(target)
        return () => observer.disconnect()
    }, [hasResults])

    const savedFor = (dealer) =>
        saved.find((d) => String(d.external_id) === String(dealer.external_id))

    const toggleSave = (dealer) => {
        const record = savedFor(dealer)
        if (record) removeDealership(record.id)
        else saveDealership(dealer)
    }

    const resultsLabel = makeFilter ? `${makeFilter} Dealerships` : 'All Dealerships'

    const renderResults = () => {
        if (isLoading && dealers.length === 0) {
            return <div className="dm-state"><CircularProgress size={24}/></div>
        }
        if (error) {
            return <div className="dm-state dm-state--error">Something went wrong. Try again.</div>
        }
        if (dealers.length === 0) {
            return <div className="dm-state">No dealerships match your filters.</div>
        }
        return (
            <>
                {dealers.map((dealer) => {
                    const isSaved = Boolean(savedFor(dealer))
                    const location = [dealer.city, dealer.state].filter(Boolean).join(', ')
                    return (
                        <div className="dm-dealer-row" key={dealer.external_id}>
                            <div className="dm-dealer-info">
                                <div className="dm-dealer-name">{dealer.name || dealer.external_id}</div>
                                <div className="dm-dealer-meta">
                                    {dealer.makes?.slice(0, 3).map((make) => (
                                        <span className="dm-brand-tag" key={make}>{make}</span>
                                    ))}
                                    {location && <span className="dm-dealer-loc">{location}</span>}
                                </div>
                            </div>
                            <button
                                type="button"
                                className={classnames('dm-select-btn', {'dm-select-btn--selected': isSaved})}
                                onClick={() => toggleSave(dealer)}
                            >
                                {isSaved ? <CheckIcon/> : <AddIcon/>}
                                {isSaved ? 'Added' : 'Select'}
                            </button>
                        </div>
                    )
                })}
                {
                    isLoading && dealers.length > 0 &&
                    <div className="dm-more-loading"><CircularProgress size={20}/></div>
                }
                <div ref={sentinelRef} className="dm-sentinel" aria-hidden="true"/>
            </>
        )
    }

    return (
        <BaseModal isOpen={isOpen} setIsOpen={setIsOpen} style={wrapperStyle}>
            <div className="DealershipsModal" data-theme={theme}>

                {/* ── Header ── */}
                <div className="dm-header">
                    <div className="dm-title-wrap">
                        <div className="dm-title">Add Dealerships</div>
                        <div className="dm-subtitle">Find dealerships to add to your network</div>
                    </div>
                    <button type="button" className="dm-icon-btn" aria-label="Close" onClick={() => setIsOpen(null)}>
                        <CloseIcon fontSize="small"/>
                    </button>
                </div>

                {/* ── Body: search/results (left) + My Dealerships (right) ── */}
                <div className="dm-body">
                    <div className="dm-main">
                        {/* Filters */}
                        <div className="dm-filters">
                            <div className="dm-search">
                                <SearchIcon className="dm-search-icon"/>
                                <input
                                    type="text"
                                    value={query}
                                    placeholder="Search by name"
                                    onChange={(e) => setQuery(e.target.value)}
                                />
                            </div>

                            <div className="dm-filter-row">
                                <div className="dm-field">
                                    <span className="dm-field-label">Brand</span>
                                    <Select
                                        className="dm-select"
                                        value={makeFilter}
                                        onChange={(e) => setMakeFilter(e.target.value)}
                                        displayEmpty
                                        MenuProps={selectMenuProps}
                                    >
                                        <MenuItem value="">All Brands</MenuItem>
                                        {BRAND_OPTIONS.map((brand) => <MenuItem key={brand} value={brand}>{brand}</MenuItem>)}
                                    </Select>
                                </div>

                                <div className="dm-field">
                                    <span className="dm-field-label">State</span>
                                    <Select
                                        className="dm-select"
                                        value={stateFilter}
                                        onChange={(e) => setStateFilter(e.target.value)}
                                        displayEmpty
                                        MenuProps={selectMenuProps}
                                    >
                                        <MenuItem value="">All States</MenuItem>
                                        {US_STATES.map((code) => <MenuItem key={code} value={code}>{code}</MenuItem>)}
                                    </Select>
                                </div>

                                <button type="button" className="dm-btn-search" onClick={() => runSearch(0, false)}>
                                    <SearchIcon fontSize="small"/> Search
                                </button>
                            </div>
                        </div>

                        {/* Results */}
                        <div className="dm-results-head">
                            <span>{resultsLabel}</span>
                            <span className="dm-results-count">{count} result{count === 1 ? '' : 's'}</span>
                        </div>
                        <div className="dm-results" ref={resultsRef}>
                            {renderResults()}
                        </div>
                    </div>

                    {/* My Dealerships */}
                    <div className="dm-side">
                        <div className="dm-side-header">
                            <div className="dm-side-title">My Dealerships</div>
                            <div className="dm-side-subtitle">
                                {saved.length} dealer{saved.length === 1 ? '' : 's'} in your network
                            </div>
                        </div>
                        <div className="dm-side-list">
                            {
                                saved.length === 0
                                    ? <div className="dm-side-empty">
                                        <StorefrontIcon/>
                                        <div className="dm-side-empty-title">No dealerships yet</div>
                                        <div className="dm-side-empty-sub">Select dealerships to add them here</div>
                                      </div>
                                    : saved.map((d) => (
                                        <div className="dm-side-item" key={d.id}>
                                            <div className="dm-side-item-info">
                                                <div className="dm-side-item-name">{d.name || d.external_id}</div>
                                                {
                                                    (d.city || d.state) &&
                                                    <div className="dm-side-item-loc">
                                                        {[d.city, d.state].filter(Boolean).join(', ')}
                                                    </div>
                                                }
                                            </div>
                                            <button
                                                type="button"
                                                className="dm-side-item-remove"
                                                aria-label="Remove dealership"
                                                onClick={() => removeDealership(d.id)}
                                            >
                                                <CloseIcon style={{fontSize: 16}}/>
                                            </button>
                                        </div>
                                    ))
                            }
                        </div>
                    </div>
                </div>

                {/* ── Footer ── */}
                <div className="dm-footer">
                    <div className="dm-footer-count">
                        <strong>{saved.length}</strong>&nbsp;dealership{saved.length === 1 ? '' : 's'} added
                    </div>
                    <button type="button" className="dm-btn-done" onClick={() => setIsOpen(null)}>Done</button>
                </div>
            </div>
        </BaseModal>
    )
}
